import type { AxiosError } from 'axios'
import type { ReactNode } from 'react'

import type { SerializedNode } from '@craftjs/core'
import type { BadgeProps } from '@wartek-id/badge'

/**
 * Types
 */

export enum JenjangValid {
  SLB = 'slb',
  PAUD = 'paud',
  SD = 'sd',
  SMP = 'smp',
  SMK = 'smk',
  SMA = 'sma',
  NONJENJANG = '',
}

export interface TopicSpeedKV {
  generic_info: string
  topics: {
    id: string
    recommendations: {
      aksi_nyata: string
      pelajari_semua_modul: string
      rekomendasi_penyelesaian_topik: string
    }
  }[]
}

export type ActivityType = 'text' | 'video' | 'quiz'

export type AksiNyataAcceptedFormats = 'video' | 'pdf' | 'image'

export type AksiNyataActionType = 'CHOOSE' | 'CONFIRM' | 'DRAFT' | 'PUBLISH'

export enum AksiNyataSessionState {
  NotCreated = 'NOT_CREATED',
  Created = 'CREATED',
  Confirmed = 'CONFIRMED',
  Completed = 'COMPLETED', // aksi nyata sudah di-publish oleh guru, dan akan/sedang dalam proses evaluasi
  Undetermined = 'UNDETERMINED',
  NeedRevision = 'NEED_REVISION', // aksi nyata tidak lulus proses evaluasi dan perlu diperbaikin oleh guru
  Validated = 'VALIDATED', // aksi nyata lulus proses evaluasi dan guru dapat masuk ke proses untuk memproses pembuatan sertifikat
  Appeal = 'APPEAL', // aksi nyata yang need revision sedang dilakukan banding oleh user
}

export type PostTestState = 'not_started' | 'not_passed' | 'passed'

export type QuizType = 'multiple' | 'single'

// imported from guru-frontend - app/KomunitasPage/partials/Detail/CommunicationList.tsx
export type TCommunicationType = 'FB' | 'TELEGRAM' | 'WA'

/**
 * Common Request Error Response
 */
interface MILError {
  code: string
  message: string
}

export interface MilErrorResponse {
  errors: MILError[]
  meta?: any
}

export interface MilUseQueryError extends AxiosError {
  originalError: {
    response: {
      data: Partial<MilErrorResponse>
      status: number
    }
  }
}

// For better type naming
export type TMilCommonErrorResponse = MilUseQueryError

/**
 * Response for useGetTopicDetail
 */
export interface TopicDetails {
  modules: Module[]
  name: string
  values: string[]
  aksiNyataOptionCount?: number
  contextualizedPageId?: number
}

/**
 * Aksi Nyata Related Types
 */

/**
 * Response for useGetTopicAksiNyataOptions
 */
export interface AksiNyataOption {
  aksiNyataOptionId: number
  title: string
  guidance: string
  descriptionQuestion: string
  reflectionQuestion: string
  acceptedFormats: AksiNyataAcceptedFormats[]
}

export interface AksiNyataCreator {
  id?: number | string
  avatarUrl: string
  email: string
  name: string
}

export interface AksiNyataAttachment {
  id: number
  type: string
  name?: string
  url?: string
}

/**
 * Response for useGetTopicAksiNyataSession
 */
export interface AksiNyataSession {
  id?: string | number
  description: string
  reflection: string
  selectedOption: AksiNyataOption
  state: AksiNyataSessionState
  title: string
  attachments: AksiNyataAttachment[] | []
  creator: AksiNyataCreator
  reviewerName: string
  reviewerUserID: string
}

export interface PublicAksiNyata extends Omit<AksiNyataSession, 'creator'> {
  feedbackFormId: number | string
  author?: AksiNyataCreator
  creator?: AksiNyataCreator
}

/**
 * Request body for usePostAksiNyata
 */
export interface AksiNyataCreateBody {
  aksiNyataOptionId?: number
  action?: AksiNyataActionType
  title?: string
  description?: string
  reflection?: string
  aksiNyataAttachmentIds?: number[]
  selectedFormat?: string
}

/**
 * Response for useGetMateriText
 */
export interface ResponseMaterialTextData {
  id: string
  name: string
  contents: string[]
}

/**
 * Response for useGetMateriVideo
 */
export interface ResponseMaterialVideoData {
  id: string
  name: string
  url: string
  description: string
}

/**
 * Response for useGetQuiz
 */
export interface ResponseQuiz {
  name: string
  questions: Question[]
  completed?: boolean
  progress?: 'NOT_STARTED' | 'COMPLETED' | 'SHOULD_RETRY'
}

/**
 * Response for useGetReflection
 */
export interface MateriReflection {
  question: string
  answer: string
  completed: boolean
}

/**
 * Response for usePostRefleksi
 */
export interface PostRefleksiResponse {
  data: {
    ID: string
    reflective: { answer: string }
  }
}

/**
 * Request body for usePostQuizProgress
 */
export interface QuizAnswer {
  questionId: number | null
  answer: string | string[]
  isCorrect?: boolean
}

/**
 * Response for useGetModule Preview
 */
export interface ResponseModulData {
  id: string
  name: string
  description: string
  duration: number
  reviewer: ModuleReviewer
  imageUrl?: string
  learningMaterial: {
    completed: boolean
    count: number
    materials: Material[]
    progress: 'NOT_STARTED' | 'COMPLETED' | 'SHOULD_RETRY'
  }
  postTest: {
    name: string
    state: PostTestState
    submittedTime: string
  }
}

/**
 * Response for useGetTopicList
 * Note: Percentage is soon deprecated and removed from response
 */
export interface Topic {
  id: string | number
  name: string
  duration: number
  totalModule: number
  description: string
  headerImage: string
  certifiable: boolean
  href?: string
  percentage?: number
  badge?: {
    state?: 'validated' | 'ongoing' | 'completed'
    type: BadgeProps['variant']
    text: string
  }
}

/**
 * Response for usePostQuizProgress & useGetMaterialByModule
 */
export interface MaterialDataResponse {
  id: string
  name: string
  completed: boolean
  reflective: {
    question: string
    answer: string
    completed?: boolean
  }
  contents: MateriContent[]
  quiz: ResponseQuiz
  shouldRetry: boolean
}

/**
 * Progress Related Interfaces
 */
export enum PostTestStatus {
  NotStarted = 'not_started',
  Passed = 'passed',
  NotPassed = 'not_passed',
}

export interface ModuleProgress {
  moduleId: number
  material: boolean
  postTest:
    | PostTestStatus.NotStarted
    | PostTestStatus.NotPassed
    | PostTestStatus.Passed
    | boolean
  prerequisiteCompleted: boolean
  imagePath: string
  name: string
}

/**
 * Response for useGetTopicsProgress
 */
export interface OverallProgress {
  id: string | number
  percentage?: number
  name: string
  imageUrl: string
  modules: ModuleProgress[]
  aksiNyata: AksiNyataSession
}

/**
 * Props Related Interfaces
 */
export interface ActivityItemProps {
  name: string
  href: string
  type: ActivityType
  isComplete?: boolean
  isShouldRetry?: boolean
  trackingParams?: {
    topik_id?: number | string
    module_id?: number | string
    materi_id?: number | string
  }
  isActive?: boolean
}

export interface ConfirmNavigateAwayDialogProps {
  title?: string
  description?: string
  children?: ReactNode
  cancelText?: string
  confirmText?: string
  isOpen: boolean
  icon?: ReactNode
  onClose: () => void
  onConfirmed: () => void
}

export interface Material {
  id: string
  name: string
  isComplete?: boolean
  completed?: boolean
}

export interface MateriContent {
  id: number
  name: string
  type: ActivityType
  completed?: boolean // deprecated
  progress?: 'NOT_STARTED' | 'COMPLETED' | 'SHOULD_RETRY'
}

export interface Module {
  id: string | number
  name: string
  duration: number
  totalMaterial: number
  number?: number
  image?: string //Note: deprecated as on latest design
  progress?: ModuleProgress | undefined
  badgeText?: string | undefined
}

export interface ModuleReviewer {
  imageUrl?: string
  name: string
  about: string
}

export interface QuizOption {
  key: string
  text: string
  isCorrect?: boolean
}

export interface Question {
  id: number
  name: string
  text: string
  type: QuizType
  answerOptions: QuizOption[]
  answer?: {
    key: string
    text: string
  }
  IsCorrect?: boolean
}

export interface QuestionIndicatorProps {
  currentIndex: number
  totalQuestions: number
}

export interface QuizOptionsProps {
  options: QuizOption[]
  onSelected: (hasAnswer: boolean, answer: string | string[]) => void
  page?: number
  showAnswers?: boolean
}

export enum AppealState {
  NotCreated = 'NOT_CREATED',
  Submitted = 'SUBMITTED',
  Accepted = 'ACCEPTED',
  Declined = 'DECLINED',
}

export type TAPIGetAksiNyataRevisionResponse = {
  topicId: string
  revisionItems: string[]
  appeal: {
    state: AppealState
    quota: number
    review: string
  }
}

export interface TabbedNavItem {
  name: string
  label: string
  href?: string
  image?: string
}

// Contextualized Learning / Kontekstualisasi Belajar / CL / KB
export enum CLWidgetType {
  ExternalAssessment = 'Assessment',
  ExternalToolkit = 'Toolkit',
  InternalTopic = 'Topic',
  InternalModule = 'Module',
  InternalLearningActivity = 'AksiNyata',
  InternalPageCard = 'PageCard',
  StaticVideoCard = 'VideoCard',
  StaticHeading = 'Heading',
  StaticText = 'Text',
  StaticButton = 'Button',
  StaticDocument = 'Document',
  StaticContainer = 'Container',
  RootContentWrapper = 'ContentWrapper',
}

export interface CLWidgetObject {
  widgetNode: string
  widgetType: CLWidgetType
  responseData: Record<string, unknown>
}

export interface CLWidgetSchema {
  [anyKey: string]: SerializedNode
}

export interface CLItem {
  id: string
  title: string
  excerpt: string
  thumbnailUrl: string
  contentSchema: string
  widgetObjects: CLWidgetObject[]
}

/* topic list type */
export type TTopicList = {
  id: number
  name: string
  duration: number
  certifiable: boolean
  totalModule: number
  description: string
  headerImage: string
  contextualizePageId: number
  percentage: number
  aksiNyataState: AksiNyataSessionState
}
